import { Badge, Box, ErrorBoundary, Skeleton } from "@prismicio/editor-ui"
import { Suspense } from "react"

import { useCustomType } from "../../../app/services/customTypes"
import { useRepository } from "../../../app/services/repository"
import { statusDetails } from "../../DocumentSearchContent"
import type { Filter } from "./FilterDropdown"
import { getAuthorDisplayName } from "./utils"

interface FilterBarProps {
  selectedFilters: Filter[]
  onRemoveFilter: (filter: Filter) => void
}

export function FilterBar(props: FilterBarProps) {
  const { selectedFilters, onRemoveFilter } = props

  return (
    <Box gap={8} flexWrap="wrap">
      <Suspense fallback={<FilterBarSkeleton />}>
        <ErrorBoundary>
          {selectedFilters.map((filter) => (
            <Filter
              key={filter.value}
              filter={filter}
              onClose={() => onRemoveFilter(filter)}
            />
          ))}
        </ErrorBoundary>
      </Suspense>
    </Box>
  )
}

interface FilterProps {
  filter: Filter
  onClose: () => void
}

function Filter(props: FilterProps) {
  const { filter, onClose } = props

  const { type, value } = filter

  switch (type) {
    case "customType":
      return <CustomTypeFilter filter={filter} onClose={onClose} />
    case "author":
      return <AuthorFilter filter={filter} onClose={onClose} />
    case "tag":
      return <FilterBadge title={`Tags: ${value}`} onClose={onClose} />
    case "status":
      return (
        <FilterBadge
          title={`Status: ${statusDetails[value]?.label ?? value}`}
          onClose={onClose}
        />
      )
  }
}

function CustomTypeFilter(props: FilterProps) {
  const { filter, onClose } = props

  const customType = useCustomType(filter.value)

  return <FilterBadge title={`Type: ${customType.label}`} onClose={onClose} />
}

function AuthorFilter(props: FilterProps) {
  const { filter, onClose } = props

  const { value } = filter

  const { authors } = useRepository()

  const author = authors.find((author) => author.id === value)
  const label = author ? getAuthorDisplayName(author) : value

  return <FilterBadge title={`Author: ${label}`} onClose={onClose} />
}

interface FilterBadgeProps {
  title: string
  onClose: () => void
}

function FilterBadge(props: FilterBadgeProps) {
  const { title, onClose } = props

  return (
    <Badge
      title={title}
      color="purple"
      size="medium"
      maxWidth={250}
      onClose={onClose}
    />
  )
}

function FilterBarSkeleton() {
  return (
    <>
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
    </>
  )
}
